import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, ArrowRight } from 'lucide-react';
import { countryCodes } from '../data/mockData';

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', countryCode: '+1', phone: '', description: ''
  });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'email' || field === 'phone') setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email' }));
    }
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 10) {
      handleChange('phone', digitsOnly);
      if (digitsOnly && digitsOnly.length !== 10) {
        setErrors(prev => ({ ...prev, phone: 'Must be 10 digits' }));
      } else {
        setErrors(prev => ({ ...prev, phone: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }

    setIsSubmitting(true);
    setTimeout(() => {
      toast({ title: "Request Submitted!", description: "Our experts will contact you within 24 hours." });
      setIsSubmitting(false);
      setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
      setErrors({ email: '', phone: '' });
      onClose();
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-[900px] p-0 gap-0 overflow-hidden border-0 bg-white rounded-2xl shadow-2xl">
        <div className="flex flex-col md:flex-row">
          <div className="hidden md:block w-[40%] relative overflow-hidden rounded-l-2xl" style={{
            background: 'linear-gradient(135deg, #1a0a2e 0%, #4C1D95 100%)'
          }}>
            <div className="absolute inset-0">
              <img
                src="https://images.unsplash.com/photo-1632152053988-e94d3d77829b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBsZWdhbCUyMGRvY3VtZW50cyUyMGRlc2slMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzc1NTE5NjUyfDA&ixlib=rb-4.1.0&q=85"
                alt="Professional workspace"
                className="w-full h-full object-cover opacity-30"
              />
            </div>
            <div className="relative z-10 p-8 h-full flex flex-col justify-center">
              <h3 className="text-2xl font-black text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Protect Your Brand Today
              </h3>
              <p className="text-sm text-white/50 leading-relaxed mb-6">Expert trademark attorneys ready to secure your brand nationwide.</p>
              <div className="space-y-3">
                {['Licensed USPTO Attorneys', 'Fast 48h Filing', 'Flat-Rate Pricing'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-white/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#14B8A6]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full md:w-[60%] p-7 md:p-8">
            <div className="mb-5">
              <h2 className="text-xl font-bold text-zinc-900 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Schedule Your Free Review</h2>
              <p className="text-sm text-zinc-500">A trademark specialist will connect with you shortly.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="dialog-name" className="text-zinc-600 text-xs font-medium">Full Name *</Label>
                <Input id="dialog-name" data-testid="dialog-name-input" placeholder="Enter your full name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required className="h-10 bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] rounded-lg mt-1" />
              </div>

              <div>
                <Label htmlFor="dialog-email" className="text-zinc-600 text-xs font-medium">Email *</Label>
                <Input id="dialog-email" data-testid="dialog-email-input" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} onBlur={handleEmailBlur} required className={`h-10 bg-zinc-50 text-zinc-900 placeholder:text-zinc-400 rounded-lg mt-1 ${errors.email ? 'border-red-500' : 'border-zinc-200'} focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]`} />
                {errors.email && <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="dialog-phone" className="text-zinc-600 text-xs font-medium">Phone *</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={formData.countryCode} onValueChange={(v) => handleChange('countryCode', v)}>
                    <SelectTrigger data-testid="dialog-country-select" className="w-[100px] h-10 bg-zinc-50 border-zinc-200 text-zinc-900 rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] bg-white border-zinc-200">
                      {countryCodes.map((item) => (
                        <SelectItem key={item.code} value={item.code} className="text-zinc-900">
                          <span className="flex items-center gap-2"><span>{item.flag}</span><span>{item.code}</span></span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input id="dialog-phone" data-testid="dialog-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 bg-zinc-50 text-zinc-900 placeholder:text-zinc-400 rounded-lg ${errors.phone ? 'border-red-500' : 'border-zinc-200'} focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]`} />
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>}
              </div>

              <div>
                <Label htmlFor="dialog-desc" className="text-zinc-600 text-xs font-medium">What do you need?</Label>
                <Textarea id="dialog-desc" data-testid="dialog-description-input" placeholder="e.g. Register my brand name with the USPTO" rows={2} value={formData.description} onChange={(e) => handleChange('description', e.target.value)} className="bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] resize-none rounded-lg mt-1" />
              </div>

              <Button type="submit" data-testid="dialog-submit-button" className="w-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white font-semibold h-11 rounded-full active:scale-[0.97] shadow-lg shadow-purple-500/20" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  <>Submit Request <ArrowRight className="ml-2 w-4 h-4" /></>
                )}
              </Button>
              <p className="text-[10px] text-zinc-400 text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
